/** Canonical technical role names used by the server and stored in the `RoleKey` column.
These match the enum on the C# side, and are what appear in claims (ClaimTypes.Role and
CustomClaims.ScopedRole). */
export const SparkRoleKey = {
  CommunityAdmin: "CommunityAdmin",
  CRC: "CRC",
  DoC: "DoC",
  RecreationManager: "RecreationManager",
  SparkCoordinator: "SparkCoordinator",
  LifeStoryViewer: "LifeStoryViewer",
  RecordsViewer: "RecordsViewer"
} as const;

export type SparkRoleKey = (typeof SparkRoleKey)[keyof typeof SparkRoleKey];

/** Returns `true` if the given string is a valid `SparkRoleKey`, otherwise `false`. */
export const isSparkRoleKey = (value: string): value is SparkRoleKey => {
  return (Object.values(SparkRoleKey) as string[]).includes(value);
};

/** Maps `SparkRoleKey`s to human-readable display labels. (Compare with `SparkRoleCompactDisplayName` 
    which gives shorter more compact labels for when you're tight on space.)

    (Should match the DisplayName column in the database, so isn't even needed if you load it from there) */
export const SparkRoleFullDisplayName = {
  CommunityAdmin: "Community Administrator",
  CRC: "Community Relations Coordinator",
  DoC: "Director of Care",
  RecreationManager: "Recreation Manager",
  SparkCoordinator: "Spark Coordinator",
  LifeStoryViewer: "Life Story Viewer",
  RecordsViewer: "Records Viewer"
} as const satisfies Record<SparkRoleKey, string>;

/** Maps `SparkRoleKey`s to compact display values intended for tight UI (e.g. header/user menu). */
export const SparkRoleCompactDisplayName = {
  CommunityAdmin: "Community Admin", // 15 chars
  CRC: "CRC", //  3 chars because the full thing is 31
  DoC: "Director of Care", // 16 chars
  RecreationManager: "Rec. Manager", // 12 chars because the full thing is 18
  SparkCoordinator: "Spark Coordinator", // 17 chars
  LifeStoryViewer: "Life Story Viewer", // 17 chars
  RecordsViewer: "Recordings Viewer" // 17 chars
} as const satisfies Record<SparkRoleKey, string>;

/** Maps `SparkRoleKey`s to their corresponding ints of the `SparkRoleCode` C# enum. 

    This is used when sending roles to the server. */
export const SparkRoleCodeMap = {
  CommunityAdmin: 1,
  CRC: 2,
  DoC: 3,
  RecreationManager: 4,
  SparkCoordinator: 5,
  LifeStoryViewer: 6,
  RecordsViewer: 7
} as const satisfies Record<SparkRoleKey, number>;
